import { z } from 'zod';
/**
 * Staff, roles and invitations (M11.9, API.md §2.12, FR-ADMIN-12, SECURITY.md §4.4, §5).
 *
 * A clinic administrator may assign exactly two roles. Platform roles are granted only
 * in the super admin console, so they are absent from `AssignableRoleSchema` rather
 * than present and refused - an option a clinic can never choose is a bug waiting to be
 * filed.
 *
 * ## Why `member.role` is not what this API speaks
 *
 * Better Auth's organization plugin stores `owner` / `admin` / `member`. The platform's
 * permission model speaks `clinic_admin` / `clinic_staff` (`roleFromMembership()`).
 * This contract uses the permission names, because they are the ones SECURITY.md §5.1
 * grants permissions to and the ones the audit trail should read in.
 *
 * `owner` is a third thing the API deliberately does not expose: it is the membership
 * created when the tenant was provisioned, it maps to `clinic_admin`, and it is what
 * makes "the last administrator" a row that cannot be removed by accident.
 */
export const ASSIGNABLE_ROLES = ['clinic_staff', 'clinic_admin'];
export const AssignableRoleSchema = z.enum(ASSIGNABLE_ROLES);
/** Whether this person can sign in at all (`user.banned`). */
export const MemberStatusSchema = z.enum(['active', 'deactivated']);
export const StaffMemberSchema = z.object({
    /** Better Auth user id. An opaque string, not a uuid. */
    id: z.string(),
    name: z.string(),
    email: z.string(),
    role: AssignableRoleSchema,
    status: MemberStatusSchema,
    /** Staff MFA is mandatory (FR-ADMIN-01); false means enrolment is unfinished. */
    mfaEnrolled: z.boolean(),
    /** ISO 8601, or null for someone who has never signed in. */
    lastLoginAt: z.string().nullable(),
    /** ISO 8601 of the membership. */
    joinedAt: z.string(),
    /**
     * True for the membership the tenant was created with. It maps to `clinic_admin`
     * like any other administrator; the flag exists so a screen can explain why the
     * remove button is disabled on that row rather than merely disabling it.
     */
    isOwner: z.boolean(),
    /** True when this row is the caller. Drives "you cannot demote yourself" in the UI. */
    isSelf: z.boolean(),
});
export const StaffMemberListResponseSchema = z.object({ data: z.array(StaffMemberSchema) });
export const StaffMemberResponseSchema = z.object({ data: StaffMemberSchema });
/** `?role=&status=` on `GET /admin/users`. */
export const StaffMemberQuerySchema = z
    .object({
    role: AssignableRoleSchema.optional(),
    status: MemberStatusSchema.optional(),
})
    .strip();
/* -------------------------------------------------------------- invitations ---- */
/** `invitation.status`, narrowed to what this API produces and reads. */
export const InvitationStatusSchema = z.enum(['pending', 'accepted', 'revoked', 'expired']);
export const StaffInvitationSchema = z.object({
    id: z.string(),
    email: z.string(),
    role: AssignableRoleSchema,
    /** Derived: a `pending` row past `expiresAt` is reported as `expired`. */
    status: InvitationStatusSchema,
    invitedByName: z.string(),
    /** ISO 8601. */
    invitedAt: z.string(),
    /** ISO 8601. Seven days from issue or from the last resend (API.md §2.12). */
    expiresAt: z.string(),
    /**
     * Why the invitation email did not arrive, as an error **class** - never a token,
     * never the vendor's sentence (M9.x, `SECURITY.md` §15).
     *
     * `null` when the last send succeeded or none has been attempted. The invitation
     * stays `pending` either way: a message the platform could not hand to Mailgun says
     * nothing about whether the person may still join, and the administrator's remedy is
     * **Re-send**, which clears this.
     *
     * Optional on the wire so a client written before this field parses a response that
     * carries it, and so the field can be read without every caller constructing one.
     */
    lastSendError: z.string().nullable().optional(),
});
export const StaffInvitationListResponseSchema = z.object({
    data: z.array(StaffInvitationSchema),
});
export const StaffInvitationResponseSchema = z.object({ data: StaffInvitationSchema });
/**
 * What issuing or re-sending an invitation answers.
 *
 * `inviteUrl` is **null whenever the clinic can send email** (M9.x): the
 * `staff_invitation` notification is queued instead, the handler re-mints the link from
 * the row, and no token is ever in the response, the queue or a log.
 *
 * It is still a string for a tenant with **no enabled email install** - a development
 * clinic before Mailgun is connected, and the first minutes of a new tenant. An
 * invitation nobody can deliver is an invitation nobody can accept, so the link goes
 * back to the administrator who just created it, to send by whatever means they already
 * use. It is returned to that one caller in a response body, never logged, never in a
 * query string, and never on the list endpoint: `GET /admin/invitations` has no way to
 * produce it, so an invitation cannot be re-read by someone who did not issue it.
 */
export const IssuedInvitationResponseSchema = z.object({
    data: StaffInvitationSchema,
    /** Null when the clinic has an enabled email channel and the message was queued. */
    inviteUrl: z.string().nullable(),
});
export const CreateInvitationRequestSchema = z
    .object({
    email: z
        .email()
        .max(254)
        .transform((value) => value.trim().toLowerCase()),
    role: AssignableRoleSchema,
})
    .strict();
/** Days an invitation is valid for. API.md §2.12: "7-day expiry". */
export const INVITATION_TTL_DAYS = 7;
/* ----------------------------------------------------------- member actions ---- */
export const ChangeRoleRequestSchema = z.object({ role: AssignableRoleSchema }).strict();
/**
 * Why access was withdrawn or a second factor cleared.
 *
 * Mandatory and free text, because it is the only part of the audit row a person will
 * read a year later and the only part a system cannot infer. Ten characters is the
 * same floor impersonation uses (SECURITY.md §6.3): long enough that "x" is not a
 * reason, short enough that "left the clinic" is.
 */
export const ActionReasonSchema = z.string().trim().min(10).max(500);
export const DeactivateMemberRequestSchema = z.object({ reason: ActionReasonSchema }).strict();
export const ReactivateMemberRequestSchema = z.object({ reason: ActionReasonSchema }).strict();
/* Named for the surface: `platform.ts` has its own `ResetMfaRequestSchema` for a
 * super admin clearing another super admin's factor, which is a different act with a
 * different two-person rule (SECURITY.md §4.3). */
export const TenantResetMfaRequestSchema = z.object({ reason: ActionReasonSchema }).strict();
/* ------------------------------------------------- accepting an invitation ---- */
/**
 * `GET /public/invitations?token=` and `POST /public/invitations/accept`.
 *
 * Public because the invitee has no account yet, and tenant-scoped by the host the
 * link was opened on - the token is signed against that tenant, so a token pasted at
 * another clinic's address does not verify.
 *
 * The token is the whole credential. It is never written to a log, never put in a
 * query string the server records, and the email that carries it carries nothing else
 * (SECURITY.md §15).
 */
export const InvitationPreviewSchema = z.object({
    email: z.string(),
    role: AssignableRoleSchema,
    clinicName: z.string(),
    /** ISO 8601. */
    expiresAt: z.string(),
});
export const InvitationPreviewResponseSchema = z.object({ data: InvitationPreviewSchema });
export const InvitationTokenQuerySchema = z.object({ token: z.string().min(16).max(512) });
export const AcceptInvitationRequestSchema = z
    .object({
    token: z.string().min(16).max(512),
    name: z.string().trim().min(1).max(120),
    /** SECURITY.md §4.2: 12 to 128 characters, no composition rules. */
    password: z.string().min(12).max(128),
})
    .strict();
/**
 * What acceptance answers.
 *
 * Deliberately not a session: a staff account must enrol a second factor before it can
 * reach anything (SECURITY.md §4.3), and handing back a session here would mean the
 * one path into the product that skips the sign-in the MFA gate hangs off.
 */
export const AcceptInvitationResponseSchema = z.object({
    data: z.object({
        email: z.string(),
        role: AssignableRoleSchema,
        /** Where to sign in. The host the invitation was accepted on. */
        signInUrl: z.string(),
    }),
});
//# sourceMappingURL=tenant-users.js.map